import { useState, useEffect, useCallback } from "react";

interface SessionRating {
	lid: number;
	rating: number;
}

interface UseLessonRatingProps {
	lessonId?: number;
	initialRating?: number;
}

interface UseLessonRatingReturn {
	lessonRating: number;
	lessonRatingKey: number;
	handleSessionRating: (lid: number, rating: number) => void;
}

/**
 * Hook to manage lesson ratings within a session
 * Keeps track of ratings the user has submitted during this session
 * Falls back to the initial rating from the server when no session rating exists
 */
export const useLessonRating = ({ lessonId, initialRating = 0 }: UseLessonRatingProps): UseLessonRatingReturn => {
	const [lessonRating, setLessonRating] = useState<number>(0);
	const [lessonRatingKey, setLessonRatingKey] = useState<number>(0);
	const [sessionRatings, setSessionRatings] = useState<SessionRating[]>([]);

	// Update rating when lesson changes
	useEffect(() => {
		if (!lessonId) {
			setLessonRating(0);
			return;
		}

		// Check if user has rated this lesson in this session
		const sessionRating = sessionRatings.find((item) => item.lid === lessonId);

		if (sessionRating) {
			setLessonRating(sessionRating.rating);
		} else {
			setLessonRating(initialRating);
		}

		// Increment key to force re-render of rating component
		setLessonRatingKey((prev) => prev + 1);
	}, [lessonId, initialRating, sessionRatings]);

	const handleSessionRating = useCallback((lid: number, rating: number) => {
		setSessionRatings((prev) => {
			const existingIndex = prev.findIndex((item) => item.lid === lid);

			if (existingIndex >= 0) {
				// Replace existing rating
				const updated = [...prev];
				updated[existingIndex] = { lid, rating };
				return updated;
			} else {
				// Add new rating
				return [...prev, { lid, rating }];
			}
		});
	}, []);

	return {
		lessonRating,
		lessonRatingKey,
		handleSessionRating,
	};
};

export type { UseLessonRatingReturn };
